import { readFile } from 'node:fs/promises';
import type { Server as HttpServer } from 'node:http';
import * as https from 'node:https';
import { join, dirname } from 'node:path';
import { fileURLToPath } from 'node:url';
import { serve } from '@hono/node-server';
import { serveStatic } from '@hono/node-server/serve-static';
import { swaggerUI } from '@hono/swagger-ui';
import type { Agent } from '@mastra/core/agent';
import type { Mastra } from '@mastra/core/mastra';
import type { ApiRoute, CorsOptions } from '@mastra/core/server';
import { Tool } from '@mastra/core/tools';
import { MastraServer, setupBrowserStream, skipIfFrameworkPublic } from '@mastra/hono';
import type { HonoBindings, HonoVariables } from '@mastra/hono';
import { InMemoryTaskStore } from '@mastra/server/a2a/store';
import { findMatchingCustomRoute } from '@mastra/server/auth';
import type { Context, MiddlewareHandler as HonoMiddlewareHandler } from 'hono';
import { Hono } from 'hono';
import { compress } from 'hono/compress';
import { cors } from 'hono/cors';
import { logger } from 'hono/logger';
import { timeout } from 'hono/timeout';
import { describeRoute } from 'hono-openapi';
import type { DescribeRouteOptions } from 'hono-openapi';
import { escapeStudioHtmlValue, injectStudioHtmlConfig, normalizeStudioBase } from '../build/utils';
import { agentLearningProxyHandler } from './handlers/agent-learning';
import {
  closeRefreshStreams,
  handleClientsRefresh,
  handleTriggerClientsRefresh,
  isHotReloadDisabled,
} from './handlers/client';
import { errorHandler } from './handlers/error';
import { healthHandler } from './handlers/health';
import { restartAllActiveWorkflowRunsHandler } from './handlers/restart-active-runs';
import { rootHandler } from './handlers/root';
import type { ServerBundleOptions } from './types';
import { welcomeHtml } from './welcome';

// Get studio path from env or default to ./studio relative to cwd
const getStudioPath = () => {
  if (process.env.MASTRA_STUDIO_PATH) {
    return process.env.MASTRA_STUDIO_PATH;
  }

  let __dirname: string = '.';
  if (import.meta.url) {
    const __filename = fileURLToPath(import.meta.url);
    __dirname = dirname(__filename);
  }

  const studioPath = process.env.MASTRA_STUDIO_PATH || join(__dirname, 'studio');
  return studioPath;
};

// Use adapter type definitions
type Bindings = HonoBindings;

type Variables = HonoVariables & {
  clients: Set<{ controller: ReadableStreamDefaultController }>;
};

type SchemaApiRoute = Extract<ApiRoute, { readonly _mastraSchemaRoute: true }>;
type HonoApiRoute = Exclude<ApiRoute, SchemaApiRoute>;
type ApiRouteMiddleware = Extract<Exclude<HonoApiRoute['middleware'], undefined>, Function>;

function isSchemaApiRoute(route: ApiRoute): route is SchemaApiRoute {
  return '_mastraSchemaRoute' in route && route._mastraSchemaRoute === true;
}

const DEFAULT_CORS_ALLOW_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'OPTIONS'];
const DEFAULT_CORS_ALLOW_HEADERS = [
  'Content-Type',
  'Authorization',
  'A2A-Version',
  'x-mastra-client-type',
  'x-mastra-dev-playground',
];
const DEFAULT_CORS_EXPOSE_HEADERS = ['Content-Length', 'X-Requested-With'];

function getCorsConfig(serverCors: CorsOptions | false | undefined, credentialsDefault: boolean) {
  const userCors = serverCors && typeof serverCors === 'object' ? serverCors : undefined;
  const origin =
    userCors && 'origin' in userCors && userCors.origin
      ? userCors.origin
      : credentialsDefault
        ? (requestOrigin: string) => requestOrigin || undefined
        : '*';
  const credentials = userCors && 'credentials' in userCors ? userCors.credentials : credentialsDefault;

  return {
    origin,
    allowMethods: DEFAULT_CORS_ALLOW_METHODS,
    credentials,
    maxAge: 3600,
    ...userCors,
    allowHeaders: [...DEFAULT_CORS_ALLOW_HEADERS, ...(userCors?.allowHeaders ?? [])],
    exposeHeaders: [...DEFAULT_CORS_EXPOSE_HEADERS, ...(userCors?.exposeHeaders ?? [])],
  };
}

function getRouteCorsConfig(apiRoutes: ApiRoute[] | undefined, pathname: string, method: string) {
  const route = findMatchingCustomRoute(pathname, method, apiRoutes)?.route;
  return route && !isSchemaApiRoute(route) ? route.cors : undefined;
}

export function getToolExports(tools: Record<string, Function>[]) {
  try {
    return tools.reduce((acc, toolModule) => {
      Object.entries(toolModule).forEach(([key, tool]) => {
        if (tool instanceof Tool) {
          acc[key] = tool;
        }
      });
      return acc;
    }, {});
  } catch (err: any) {
    console.error(
      `Failed to import tools
reason: ${err.message}
${err.stack.split('\n').slice(1).join('\n')}
    `,
      err,
    );
  }
}

export async function createHonoServer(
  mastra: Mastra,
  options: ServerBundleOptions = {
    tools: {},
  },
) {
  // Register bundled tools with Mastra so they can be used by stored agents
  // This bridges the gap between tools discovered by the CLI bundler and the Mastra instance
  if (options.tools) {
    for (const [key, tool] of Object.entries(options.tools)) {
      try {
        mastra.addTool(tool as any, key);
      } catch {
        // Tool may already be registered (e.g., if defined in Mastra config), ignore
      }
    }
  }

  // Create typed Hono app
  const app = new Hono<{ Bindings: Bindings; Variables: Variables }>();
  const server = mastra.getServer();
  const apiPrefix = server?.apiPrefix ?? '/api';
  const a2aTaskStore = new InMemoryTaskStore();
  const routes = server?.apiRoutes;

  // Pre-process routes: bake hono-openapi describeRoute into route middleware
  // so the adapter handles it as normal middleware without needing to know about hono-openapi
  const processedRoutes: ApiRoute[] | undefined = routes?.map(route => {
    if (isSchemaApiRoute(route)) {
      return route;
    }

    if (route.openapi) {
      const existingMiddleware = route.middleware
        ? Array.isArray(route.middleware)
          ? route.middleware
          : [route.middleware]
        : [];
      return {
        ...route,
        middleware: [
          describeRoute(route.openapi as unknown as DescribeRouteOptions) as unknown as ApiRouteMiddleware,
          ...existingMiddleware,
        ],
      };
    }
    return route;
  });

  // Store custom route auth configurations
  const customRouteAuthConfig = new Map<string, boolean>();

  if (processedRoutes) {
    for (const route of processedRoutes) {
      // By default, routes require authentication unless explicitly set to false
      const requiresAuth = route.requiresAuth !== false;
      const routeKey = `${route.method}:${route.path}`;
      customRouteAuthConfig.set(routeKey, requiresAuth);
    }
  }

  // Set up error handling - use custom onError handler if provided, otherwise use default
  const customOnError = server?.onError;
  app.onError((err, c) => {
    if (customOnError) {
      return customOnError(err, c as unknown as Parameters<typeof customOnError>[1]);
    }
    return errorHandler(err, c, options.isDev);
  });

  // Define body limit options
  const bodyLimitOptions = {
    maxSize: server?.bodySizeLimit ?? 4.5 * 1024 * 1024, // 4.5 MB,
    onError: () => ({ error: 'Request body too large' }),
  };

  // Create server adapter with all configuration
  const honoServerAdapter = new MastraServer({
    app,
    mastra,
    tools: options.tools,
    taskStore: a2aTaskStore,
    bodyLimitOptions,
    openapiPath: options?.isDev || server?.build?.openAPIDocs ? '/openapi.json' : undefined,
    customRouteAuthConfig,
    customApiRoutes: processedRoutes,
    prefix: apiPrefix,
    mcpOptions: server?.mcpOptions,
  });

  // Register context middleware FIRST - this sets mastra, requestContext, tools, taskStore in context
  // Cast needed due to Hono type variance - safe because registerContextMiddleware is generic
  honoServerAdapter.registerContextMiddleware();

  // Apply custom server middleware from Mastra instance
  const serverMiddleware = mastra.getServerMiddleware?.();

  if (serverMiddleware && serverMiddleware.length > 0) {
    for (const m of serverMiddleware) {
      // Wrap with skipIfFrameworkPublic so user middleware cannot 401 routes
      // the framework declared public via `requiresAuth: false`
      // (e.g. Studio sign-in endpoints like /api/auth/capabilities).
      app.use(m.path, skipIfFrameworkPublic(m.handler));
    }
  }

  // Browser stream WebSocket setup - MUST be before CORS middleware
  // to avoid "can't modify immutable headers" error on WebSocket upgrade
  // This is async because it dynamically imports @hono/node-ws to avoid
  // bundling ws into user code. Returns null if ws is not available.
  const browserStreamSetup =
    options.browserStream === false
      ? null
      : await setupBrowserStream(app, {
          getToolset: async (agentId: string) => {
            // Look up agent and return its browser if configured.
            // First try the runtime registry (code-defined + previously hydrated agents),
            // then fall back to the editor for stored agents (hydrates on first access).
            // Agent-level SDK browsers live on `agent.browser`; CLI providers
            // (e.g. @mastra/browser-viewer) live on the agent's workspace.
            const resolveBrowser = async (agent: Agent | undefined | null) => {
              if (!agent) return undefined;
              if (agent.browser) return agent.browser;
              try {
                const workspace = await agent.getWorkspace();
                return workspace?.browser;
              } catch {
                return undefined;
              }
            };

            try {
              const runtimeAgent = mastra.getAgentById(agentId);
              if (runtimeAgent) {
                return await resolveBrowser(runtimeAgent);
              }
            } catch {
              // Agent not in runtime registry — try stored agents via editor
            }

            try {
              const storedAgent = await mastra.getEditor?.()?.agent.getById(agentId);
              return await resolveBrowser(storedAgent);
            } catch {
              return undefined;
            }
          },
          apiPrefix,
        });

  // Fallback session probe when browser streaming isn't available
  // (ws / @hono/node-ws not installed, disabled by deployer, or serverless environment).
  // Lets the client decide not to open a WS instead of failing the upgrade.
  if (!browserStreamSetup) {
    app.get(`${apiPrefix}/agents/:agentId/browser/session`, c =>
      c.json({ hasSession: false, screencastAvailable: false }),
    );
  }

  // Global CORS config
  if (server?.cors === false) {
    app.use('*', timeout(server?.timeout ?? 3 * 60 * 1000));
  } else {
    const hasAuth = !!server?.auth;
    app.use('*', timeout(server?.timeout ?? 3 * 60 * 1000), async (c, next) => {
      const pathname = new URL(c.req.url).pathname;
      const method =
        c.req.method === 'OPTIONS' ? (c.req.header('Access-Control-Request-Method') ?? c.req.method) : c.req.method;
      const routeCors = getRouteCorsConfig(processedRoutes, pathname, method);

      const corsOptions = routeCors ? getCorsConfig(routeCors, false) : getCorsConfig(server?.cors, hasAuth);
      return cors(corsOptions as unknown as Parameters<typeof cors>[0])(c, next);
    });
  }

  // Health check endpoint (before auth middleware so it's publicly accessible)
  app.get(
    '/health',
    describeRoute({
      description: 'Health check endpoint',
      tags: ['system'],
      responses: {
        200: {
          description: 'Service is healthy',
        },
      },
    }),
    healthHandler,
  );

  if (options?.isDev && options.studio) {
    app.get('/api/learning/*', agentLearningProxyHandler);
  }

  if (options?.isDev || server?.build?.swaggerUI) {
    app.get(
      apiPrefix,
      describeRoute({
        description: 'API Welcome Page',
        tags: ['system'],
        responses: {
          200: {
            description: 'Success',
          },
        },
      }),
      rootHandler,
    );
  }

  // Validate EE license before starting (checks RBAC config vs license)
  await honoServerAdapter.validateEELicense();

  // Register auth middleware (authentication and authorization)
  // This is handled by the server adapter now
  honoServerAdapter.registerAuthMiddleware();

  if (server?.middleware) {
    const normalizedMiddlewares = Array.isArray(server.middleware) ? server.middleware : [server.middleware];
    const middlewares = normalizedMiddlewares.map(middleware => {
      if (typeof middleware === 'function') {
        return {
          path: '*',
          handler: middleware,
        };
      }

      return middleware;
    });

    for (const middleware of middlewares) {
      // Wrap with skipIfFrameworkPublic so user middleware cannot 401 routes
      // the framework declared public via `requiresAuth: false`
      // (e.g. Studio sign-in endpoints like /api/auth/capabilities).
      app.use(middleware.path, skipIfFrameworkPublic(middleware.handler as unknown as HonoMiddlewareHandler));
    }
  }

  // Register custom API routes via the adapter (auth + middleware handled uniformly)
  await honoServerAdapter.registerCustomApiRoutes();

  if (server?.build?.apiReqLogs) {
    app.use(logger());
  }

  // Register adapter routes (adapter was created earlier with configuration)
  // Cast needed due to Hono type variance - safe because registerRoutes is generic
  await honoServerAdapter.registerRoutes();

  if (options?.isDev || server?.build?.swaggerUI) {
    // Warn if Swagger UI is enabled but OpenAPI docs are not in production
    if (!options?.isDev && server?.build?.swaggerUI && !server?.build?.openAPIDocs) {
      const logger = mastra.getLogger();
      logger.warn(
        'Swagger UI is enabled but OpenAPI documentation is disabled. ' +
          'The Swagger UI will not function properly without the OpenAPI endpoint. ' +
          'Please enable openAPIDocs in your server.build configuration:\n' +
          '  server: { build: { swaggerUI: true, openAPIDocs: true } }',
      );
    }

    app.get(
      '/swagger-ui',
      describeRoute({
        hide: true,
      }),
      swaggerUI({ url: `${apiPrefix}/openapi.json` }),
    );
  }

  if (options?.isDev) {
    app.post(
      '/__restart-active-workflow-runs',
      describeRoute({
        hide: true,
      }),
      restartAllActiveWorkflowRunsHandler,
    );
  }

  const serverOptions = mastra.getServer();
  const studioBasePath = normalizeStudioBase(serverOptions?.studioBase ?? '/');

  if (options?.studio) {
    // SSE endpoint for refresh notifications
    app.get(
      `${studioBasePath}/refresh-events`,
      describeRoute({
        hide: true,
      }),
      handleClientsRefresh,
    );

    // Trigger refresh for all clients
    app.post(
      `${studioBasePath}/__refresh`,
      describeRoute({
        hide: true,
      }),
      handleTriggerClientsRefresh,
    );

    // Check hot reload status
    app.get(
      `${studioBasePath}/__hot-reload-status`,
      describeRoute({
        hide: true,
      }),
      (c: Context) => {
        return c.json({
          disabled: isHotReloadDisabled(),
          timestamp: new Date().toISOString(),
        });
      },
    );

    // Enable gzip/deflate compression for studio static assets only
    app.use(`${studioBasePath}/assets/*`, compress());

    // Studio routes - these should come after API routes
    // Serve static assets from studio directory
    // Note: Vite builds with base: './' so all asset URLs are relative
    // The <base href> tag in index.html handles path resolution for the SPA
    const studioPath = getStudioPath();
    app.use(
      `${studioBasePath}/assets/*`,
      serveStatic({
        root: join(studioPath, 'assets'),
        rewriteRequestPath: path => {
          // Remove the basePath AND /assets prefix to get the actual file path
          // Example: /custom-path/assets/style.css -> /style.css -> ./studio/assets/style.css
          let rewritten = path;
          if (studioBasePath && rewritten.startsWith(studioBasePath)) {
            rewritten = rewritten.slice(studioBasePath.length);
          }
          // Remove the /assets prefix since root is already './studio/assets'
          if (rewritten.startsWith('/assets')) {
            rewritten = rewritten.slice('/assets'.length);
          }
          return rewritten;
        },
      }),
    );
  }

  // Dynamic HTML handler - this must come before static file serving
  app.get('*', async (c, next) => {
    const requestPath = c.req.path;

    // Skip if it's an API route
    if (
      requestPath === apiPrefix ||
      requestPath.startsWith(`${apiPrefix}/`) ||
      requestPath.startsWith('/swagger-ui') ||
      requestPath.startsWith('/openapi.json')
    ) {
      return await next();
    }

    // Skip if it's an asset file (has extension other than .html)
    if (requestPath.includes('.') && !requestPath.endsWith('.html')) {
      return await next();
    }

    // Only serve studio for routes matching the configured base path
    const isStudioRoute =
      studioBasePath === '' || requestPath === studioBasePath || requestPath.startsWith(`${studioBasePath}/`);
    if (options?.studio && isStudioRoute) {
      // For HTML routes, serve index.html with dynamic replacements
      const studioPath = getStudioPath();
      let indexHtml = await readFile(join(studioPath, 'index.html'), 'utf-8');

      // Inject the server configuration into index.html placeholders
      const port = serverOptions?.port ?? (Number(process.env.PORT) || 4111);
      const hideCloudCta = process.env.MASTRA_HIDE_CLOUD_CTA === 'true';
      const bindHost = serverOptions?.host ?? process.env.MASTRA_HOST;
      const host = bindHost ?? 'localhost';
      const key =
        serverOptions?.https?.key ??
        (process.env.MASTRA_HTTPS_KEY ? Buffer.from(process.env.MASTRA_HTTPS_KEY, 'base64') : undefined);
      const cert =
        serverOptions?.https?.cert ??
        (process.env.MASTRA_HTTPS_CERT ? Buffer.from(process.env.MASTRA_HTTPS_CERT, 'base64') : undefined);
      const protocol = key && cert ? 'https' : 'http';
      // Studio host/protocol/port for Studio URL injection — allows bind address
      // (e.g. 0.0.0.0) to differ from the domain browsers should connect to
      const studioHost = serverOptions?.studioHost ?? host;
      const studioProtocol = serverOptions?.studioProtocol ?? protocol;
      const studioPort = serverOptions?.studioPort ?? port;

      const cloudApiEndpoint = process.env.MASTRA_CLOUD_API_ENDPOINT || '';
      const experimentalFeatures = process.env.EXPERIMENTAL_FEATURES === 'true' ? 'true' : 'false';
      const experimentalUI = process.env.MASTRA_EXPERIMENTAL_UI === 'true' ? 'true' : 'false';
      const templatesEnabled = process.env.MASTRA_TEMPLATES === 'true' ? 'true' : 'false';
      const agentSignals = process.env.MASTRA_AGENT_SIGNALS === 'false' ? 'false' : 'true';
      const signalsUI = process.env.MASTRA_SIGNALS_UI === 'true' ? 'true' : 'false';
      const organizationId = process.env.MASTRA_ORGANIZATION_ID || '';
      const platformProjectId = process.env.MASTRA_PLATFORM_PROJECT_ID || '';
      const platformObservabilityEndpoint = process.env.MASTRA_PLATFORM_OBSERVABILITY_ENDPOINT || '';
      const requestContextPresets = process.env.MASTRA_REQUEST_CONTEXT_PRESETS || '';

      const autoDetectUrl = process.env.MASTRA_AUTO_DETECT_URL === 'true';

      indexHtml = injectStudioHtmlConfig(indexHtml, {
        host: `'${studioHost}'`,
        port: `'${studioPort}'`,
        protocol: `'${studioProtocol}'`,
        apiPrefix: `'${serverOptions?.apiPrefix ?? '/api'}'`,
        basePath: studioBasePath,
        hideCloudCta: `'${hideCloudCta}'`,
        cloudApiEndpoint: `'${escapeStudioHtmlValue(cloudApiEndpoint)}'`,
        experimentalFeatures: `'${experimentalFeatures}'`,
        templates: `'${templatesEnabled}'`,
        telemetryDisabled: `'${escapeStudioHtmlValue(process.env.MASTRA_TELEMETRY_DISABLED ?? '')}'`,
        requestContextPresets: `'${escapeStudioHtmlValue(requestContextPresets)}'`,
        experimentalUI: `'${experimentalUI}'`,
        agentSignals: `'${agentSignals}'`,
        signalsUI: `'${signalsUI}'`,
        organizationId: `'${escapeStudioHtmlValue(organizationId)}'`,
        platformProjectId: `'${escapeStudioHtmlValue(platformProjectId)}'`,
        platformObservabilityEndpoint: `'${escapeStudioHtmlValue(platformObservabilityEndpoint)}'`,
        autoDetectUrl: `'${autoDetectUrl}'`,
      });

      return c.newResponse(indexHtml, 200, { 'Content-Type': 'text/html' });
    }

    return c.newResponse(welcomeHtml(apiPrefix), 200, { 'Content-Type': 'text/html' });
  });

  if (options?.studio) {
    // Serve extra static files from studio directory (this comes after HTML handler)
    const studioRootPath = getStudioPath();
    const studioPath = studioBasePath ? `${studioBasePath}/*` : '*';
    app.use(
      studioPath,
      serveStatic({
        root: studioRootPath,
        rewriteRequestPath: path => {
          // Remove the basePath prefix if present
          if (studioBasePath && path.startsWith(studioBasePath)) {
            return path.slice(studioBasePath.length);
          }
          return path;
        },
      }),
    );
  }

  // Attach injectWebSocket to app for backwards compatibility
  // Consumers can use app directly, and optionally call app.injectWebSocket(server) for browser streaming
  (app as any).injectWebSocket = browserStreamSetup?.injectWebSocket;

  return app;
}

export async function createNodeServer(mastra: Mastra, options: ServerBundleOptions = { tools: {} }) {
  const app = await createHonoServer(mastra, options);
  const injectWebSocket = (app as any).injectWebSocket;
  const serverOptions = mastra.getServer();
  const apiPrefix = serverOptions?.apiPrefix ?? '/api';
  const drainTimeoutMs = serverOptions?.drainTimeout ?? 5000;
  if (
    serverOptions?.handleShutdownSignals !== false &&
    (!Number.isFinite(drainTimeoutMs) || drainTimeoutMs < 0 || drainTimeoutMs > 2_147_483_647)
  ) {
    throw new RangeError('server.drainTimeout must be a finite number between 0 and 2147483647 milliseconds');
  }

  const key =
    serverOptions?.https?.key ??
    (process.env.MASTRA_HTTPS_KEY ? Buffer.from(process.env.MASTRA_HTTPS_KEY, 'base64') : undefined);
  const cert =
    serverOptions?.https?.cert ??
    (process.env.MASTRA_HTTPS_CERT ? Buffer.from(process.env.MASTRA_HTTPS_CERT, 'base64') : undefined);
  const isHttpsEnabled = Boolean(key && cert);

  const bindHost = serverOptions?.host ?? process.env.MASTRA_HOST;
  const host = bindHost ?? 'localhost';
  const port = serverOptions?.port ?? (Number(process.env.PORT) || 4111);
  const protocol = isHttpsEnabled ? 'https' : 'http';
  const studioHost = serverOptions?.studioHost ?? host;
  const studioProtocol = serverOptions?.studioProtocol ?? protocol;
  const studioPort = serverOptions?.studioPort ?? port;

  const server = serve(
    {
      fetch: app.fetch,
      port,
      hostname: bindHost,
      ...(isHttpsEnabled
        ? {
            createServer: https.createServer,
            serverOptions: {
              key,
              cert,
            },
          }
        : {}),
    },
    () => {
      const logger = mastra.getLogger();
      logger.info('Mastra API running', { url: `${protocol}://${host}:${port}${apiPrefix}` });
      if (options?.studio) {
        const studioBasePath = normalizeStudioBase(serverOptions?.studioBase ?? '/');
        const studioUrl = `${studioProtocol}://${studioHost}:${studioPort}${studioBasePath}`;
        logger.info('Studio available', { url: studioUrl });
      }

      if (process.send) {
        process.send({
          type: 'server-ready',
          port,
          host,
        });
      }
    },
  );

  // Enable WebSocket support for browser streaming (if available)
  // MUST be called after serve() returns per @hono/node-ws requirements
  injectWebSocket?.(server);

  // Backwards compatibility for projects running a newer deployer/CLI with an older @mastra/core.
  // TODO(v2): call `mastra.startWorkers()` unconditionally once old core versions are unsupported.
  const workerLifecycle = mastra as unknown as {
    startWorkers?: () => Promise<void>;
    startEventEngine: () => Promise<void>;
  };
  if (typeof workerLifecycle.startWorkers === 'function') {
    await workerLifecycle.startWorkers();
  } else {
    await workerLifecycle.startEventEngine();
  }

  // Fire-and-forget anonymous token usage telemetry (respects MASTRA_TELEMETRY_DISABLED).
  // Dynamic import keeps compatibility with older @mastra/core versions without the
  // `@mastra/core/telemetry` entry point.
  void import('@mastra/core/telemetry').then(({ syncUsageTelemetry }) => syncUsageTelemetry(mastra)).catch(() => {});
  void import('@mastra/core/telemetry')
    .then(({ syncFeatureUsageTelemetry }) => syncFeatureUsageTelemetry(mastra))
    .catch(() => {});

  // Graceful shutdown so in-flight requests drain and storage backends release
  // resources (e.g. DuckDB's native file lock) before the process exits. On
  // `mastra dev` hot reloads the old process is sent SIGINT; without this the
  // lock can linger and the restarted process fails with "Conflicting lock is
  // held". The drain window is configurable via `server.drainTimeout` so
  // rolling deploys can let long-running agent turns finish.
  if (serverOptions?.handleShutdownSignals !== false) {
    // Core teardown keeps its historic 5s bound regardless of the drain
    // window: it must always run (e.g. DuckDB's file lock release), but a
    // hanging shutdown can't be allowed to block process exit.
    const CORE_SHUTDOWN_TIMEOUT_MS = 5000;
    const timedOut = Symbol('shutdown-timeout');
    const race = async (work: Promise<unknown>, ms: number): Promise<unknown> => {
      let timeout: ReturnType<typeof setTimeout> | undefined;
      const timeoutPromise = new Promise<typeof timedOut>(resolve => {
        timeout = setTimeout(() => resolve(timedOut), ms);
      });
      try {
        return await Promise.race([work, timeoutPromise]);
      } finally {
        clearTimeout(timeout);
      }
    };
    let shuttingDown = false;
    const shutdown = async (signal: NodeJS.Signals) => {
      if (shuttingDown) {
        return void process.exit(1);
      }
      shuttingDown = true;
      const logger = mastra.getLogger();
      logger.info('Shutting down Mastra server', { signal });
      try {
        // Phase 1: stop accepting new connections and wait for in-flight
        // requests (including active streams) to finish, bounded by the drain
        // window. Studio's hot-reload SSE streams never end on their own, so
        // close them first; idle keep-alive sockets would also hold the close
        // callback open, so close those explicitly (feature-detected —
        // available on Node http(s) servers since 18.2). Draining before core
        // teardown prevents storage being closed underneath in-flight
        // requests.
        const drained = new Promise<void>(resolve => {
          server.close(() => resolve());
        });
        closeRefreshStreams();
        const closeIdleConnections = () => (server as Partial<HttpServer>).closeIdleConnections?.();
        closeIdleConnections();
        // A keep-alive socket that goes idle only after its in-flight response
        // finishes is not caught by the initial sweep above — the client keeps
        // it open and the drain would stall until the full timeout. Sweep
        // periodically so the server exits as soon as work actually finishes.
        const idleSweep = setInterval(closeIdleConnections, 100);
        idleSweep.unref?.();
        try {
          if ((await race(drained, drainTimeoutMs)) === timedOut) {
            logger.warn('Mastra server drain timed out; closing remaining HTTP connections', {
              timeoutMs: drainTimeoutMs,
            });
            // Node does not include upgraded protocols such as WebSocket here;
            // those sockets are terminated when the process exits below.
            (server as Partial<HttpServer>).closeAllConnections?.();
          }
        } finally {
          clearInterval(idleSweep);
        }
      } catch (error) {
        logger.error('Error while draining Mastra server', { error });
      }

      // Phase 2: always tear down core (workers, durable runs, storage), even
      // if draining failed or timed out. Feature-detect for older @mastra/core
      // versions without shutdown().
      try {
        const lifecycle = mastra as unknown as { shutdown?: () => Promise<void> };
        if (typeof lifecycle.shutdown === 'function') {
          if ((await race(lifecycle.shutdown(), CORE_SHUTDOWN_TIMEOUT_MS)) === timedOut) {
            logger.warn('Mastra shutdown timed out; forcing exit', { timeoutMs: CORE_SHUTDOWN_TIMEOUT_MS });
          }
        }
      } catch (error) {
        logger.error('Error during Mastra shutdown', { error });
      }
      process.exit(0);
    };
    // Keep both listeners active so any second shutdown signal can force an
    // immediate exit, regardless of whether it matches the first signal.
    process.on('SIGINT', () => void shutdown('SIGINT'));
    process.on('SIGTERM', () => void shutdown('SIGTERM'));
  }

  return server;
}
